import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useApi } from "../lib/api";
import { useAuth } from "../lib/auth";

type Brand = "ben-e-fit" | "medialine" | "ki-guru" | "custom";

type TenantCreated = {
  id: string;
  slug: string;
  display_name: string;
  brand: string;
  keycloak_realm: string;
  data_classification_default: string;
  created_at: string;
};

const INDUSTRIES: Array<{ slug: string; label: string; brand: Brand }> = [
  { slug: "consulting",   label: "Beratung / Consulting",   brand: "ben-e-fit" },
  { slug: "it-services",  label: "IT-Services / SaaS",      brand: "medialine" },
  { slug: "b2b-saas",     label: "B2B SaaS",                brand: "ki-guru" },
  { slug: "finance",      label: "Finanzdienstleistung",    brand: "custom" },
  { slug: "manufacturing",label: "Produktion / Industrie",  brand: "custom" },
  { slug: "trade",        label: "Handel / Retail",         brand: "custom" },
  { slug: "energy",       label: "Energie / Versorgung",    brand: "custom" },
  { slug: "healthcare",   label: "Gesundheitswesen",        brand: "custom" },
  { slug: "logistics",    label: "Logistik / Transport",    brand: "custom" },
  { slug: "construction", label: "Bau / Immobilien",        brand: "custom" },
  { slug: "education",    label: "Bildung",                 brand: "custom" },
  { slug: "public",       label: "Oeffentliche Verwaltung", brand: "custom" },
  { slug: "other",        label: "Andere Branche",          brand: "custom" },
];

export default function OnboardingPage() {
  const api = useApi();
  const { tenantId } = useAuth();
  const [step, setStep] = useState(1);
  const [companyName, setCompanyName] = useState("");
  const [slugInput, setSlugInput] = useState("");
  const [industry, setIndustry] = useState(INDUSTRIES[0]);
  const [adminEmail, setAdminEmail] = useState("");
  const [classification, setClassification] = useState<"internal" | "confidential">("internal");
  const [externalLlm, setExternalLlm] = useState(false);
  const [result, setResult] = useState<TenantCreated | null>(null);

  // Auto-slug aus Firmenname
  const autoSlug = companyName.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 16);
  const effectiveSlug = (slugInput || autoSlug).toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 16);
  const tenantId_target = `tnt_${effectiveSlug}`;
  const validSlug = /^[a-z0-9]{4,32}$/.test(effectiveSlug);

  const createTenant = useMutation({
    mutationFn: () =>
      api.post<TenantCreated>("/tenants", {
        slug: effectiveSlug,
        display_name: companyName,
        brand: industry.brand,
        admin_email: adminEmail || "admin@example.com",
        external_llm_allowed: externalLlm,
      }),
    onSuccess: (data) => {
      setResult(data);
      setStep(4);
    },
  });

  const isDemoTenant = tenantId === "tnt_demo";

  if (!isDemoTenant && step < 4) {
    return (
      <div className="page">
        <h1>Onboarding</h1>
        <div className="card alert">
          <div className="card-title">Bereits onboarded</div>
          <p>
            Du bist bereits einem Tenant zugeordnet (<code>{tenantId}</code>).
            Der Onboarding-Wizard ist nur für neu registrierte Demo-User
            (<code>tnt_demo</code>) sichtbar.
          </p>
          <p>
            Wenn du einen <strong>weiteren Tenant</strong> anlegen willst (z.B. für
            einen Kunden), muss deine Rolle <code>platform_admin</code> sein.
            Wende dich an den Plattform-Administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page onboarding">
      <h1>Tenant Onboarding</h1>
      <p className="subtitle">
        Willkommen! Lass uns deinen eigenen KPI-Mining-Mandanten in 3 Schritten anlegen.
        Es dauert &lt; 4 Stunden bis zu deinem ersten Briefing.
      </p>

      <div className="onboarding-progress">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className={`step-pill ${step >= s ? "active" : ""} ${step === s ? "current" : ""}`}>
            {s === 4 ? "DONE" : `Schritt ${s}`}
          </div>
        ))}
      </div>

      {step === 1 && (
        <section className="onboarding-step">
          <h2>1. Wer bist du?</h2>
          <label>
            <span>Firmenname</span>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="z.B. Acme Beratung GmbH"
              autoFocus
            />
          </label>
          <label>
            <span>Tenant-Slug (optional, sonst automatisch)</span>
            <input
              type="text"
              value={slugInput}
              onChange={(e) => setSlugInput(e.target.value)}
              placeholder={autoSlug || "acmeberatung"}
            />
            <small className="muted">
              {effectiveSlug ? (
                <>
                  Tenant-ID wird: <code>{tenantId_target}</code>{" "}
                  {validSlug ? "OK" : "(zu kurz — mind. 4 Zeichen)"}
                </>
              ) : (
                <>Tippe einen Namen ein, dann wird automatisch ein Slug erzeugt.</>
              )}
            </small>
          </label>
          <label>
            <span>Admin-Email (du oder ein Kollege)</span>
            <input
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="admin@deinefirma.example"
            />
          </label>
          <div className="onboarding-nav">
            <button
              className="btn primary"
              disabled={!companyName || !validSlug}
              onClick={() => setStep(2)}
            >
              Weiter zu Schritt 2
            </button>
          </div>
        </section>
      )}

      {step === 2 && (
        <section className="onboarding-step">
          <h2>2. Welche Branche?</h2>
          <p className="muted">
            Wir nutzen die Branche, um dir die richtigen KPI-Vorschläge zu machen
            (z.B. Trial-Conv für SaaS, OEE für Produktion).
          </p>
          <div className="industry-grid">
            {INDUSTRIES.map((i) => (
              <div
                key={i.slug}
                className={`industry-card ${industry.slug === i.slug ? "selected" : ""}`}
                onClick={() => setIndustry(i)}
              >
                <div className="industry-label">{i.label}</div>
                <div className="industry-brand muted small">Marken-Theme: {i.brand}</div>
              </div>
            ))}
          </div>
          <div className="onboarding-nav">
            <button className="btn ghost" onClick={() => setStep(1)}>Zurück</button>
            <button className="btn primary" onClick={() => setStep(3)}>Weiter zu Schritt 3</button>
          </div>
        </section>
      )}

      {step === 3 && (
        <section className="onboarding-step">
          <h2>3. Daten-Setup</h2>
          <label>
            <span>Standard-Klassifikation für deine Daten</span>
            <select
              value={classification}
              onChange={(e) => setClassification(e.target.value as "internal" | "confidential")}
            >
              <option value="internal">internal — kein PII, nicht öffentlich</option>
              <option value="confidential">confidential — sensibel (z.B. Financials)</option>
            </select>
            <small className="muted">
              Du kannst pro Metric später individuell hochstufen. Klassifikation steuert,
              ob externe LLM-Calls erlaubt sind.
            </small>
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={externalLlm}
              onChange={(e) => setExternalLlm(e.target.checked)}
            />
            <span>Externe LLM-Calls erlauben (OpenAI, Anthropic, Gemini)</span>
            <small className="muted">
              Nur für Demo/Sandbox. Bei <code>confidential</code> oder <code>pii</code>{" "}
              Daten wird trotz dieses Schalters lokal geroutet.
            </small>
          </label>

          <div className="card summary">
            <div className="card-title">Zusammenfassung</div>
            <ul className="metric-list">
              <li className="metric-item"><strong>Firma:</strong> {companyName}</li>
              <li className="metric-item"><strong>Tenant-ID:</strong> <code>{tenantId_target}</code></li>
              <li className="metric-item"><strong>Branche:</strong> {industry.label}</li>
              <li className="metric-item"><strong>Brand-Theme:</strong> {industry.brand}</li>
              <li className="metric-item"><strong>Admin-Email:</strong> {adminEmail || "(nicht gesetzt)"}</li>
              <li className="metric-item"><strong>Klassifikation:</strong> {classification}</li>
              <li className="metric-item"><strong>Externe LLMs:</strong> {externalLlm ? "ja" : "nein"}</li>
            </ul>
          </div>

          <div className="onboarding-nav">
            <button className="btn ghost" onClick={() => setStep(2)}>Zurück</button>
            <button
              className="btn primary"
              onClick={() => createTenant.mutate()}
              disabled={createTenant.isPending}
            >
              {createTenant.isPending ? "Erzeuge Tenant..." : "Tenant erzeugen"}
            </button>
          </div>
          {createTenant.error && (
            <p className="error">
              Fehler: {(createTenant.error as { message?: string }).message}
            </p>
          )}
        </section>
      )}

      {step === 4 && result && (
        <section className="onboarding-step">
          <h2>Tenant erfolgreich angelegt!</h2>
          <div className="card good">
            <div className="card-title">Dein neuer Tenant</div>
            <ul className="metric-list">
              <li className="metric-item"><strong>ID:</strong> <code>{result.id}</code></li>
              <li className="metric-item"><strong>Name:</strong> {result.display_name}</li>
              <li className="metric-item"><strong>Brand:</strong> {result.brand}</li>
              <li className="metric-item">
                <strong>Angelegt:</strong> {new Date(result.created_at).toLocaleString("de-DE")}
              </li>
            </ul>
          </div>

          <h3>Naechste Schritte</h3>
          <ol className="metric-list">
            <li className="metric-item">
              <strong>Daten hochladen:</strong> Geh zu <Link to="/app/sources">Quellen</Link> und
              lade eine CSV hoch oder konfiguriere einen Connector.
            </li>
            <li className="metric-item">
              <strong>KPI-Discovery starten:</strong> Der kpi-discovery-Agent schlägt
              automatisch 10-15 Kennzahlen vor, die zu deinen Daten passen.
            </li>
            <li className="metric-item">
              <strong>Erstes Briefing:</strong> Sobald 1 Discovery-Run erfolgreich
              ist, wird ein <Link to="/app/briefings">Daily-Briefing</Link> generiert.
            </li>
          </ol>

          <div className="onboarding-nav">
            <Link className="btn primary" to="/app/cockpit">
              Zum Cockpit
            </Link>
          </div>
          <p className="muted small">
            Hinweis: Der eingestellte Tenant <code>{tenantId}</code> wechselt erst beim
            nächsten Login. In Demo-Mode kannst du den X-Tenant-Id Header manuell auf{" "}
            <code>{result.id}</code> setzen.
          </p>
        </section>
      )}
    </div>
  );
}
